import { maybe, rd, RemoteData } from '@passionware/monads';
import {
  Camera,
  Clapperboard,
  FileText,
  GalleryHorizontal,
  List,
  PlusIcon
} from 'lucide-react';
import React from 'react';
import { useRouteMatch } from 'react-router-dom';
import type { EnterpriseTeam } from 'v5/api/enterprise/enterprise.api';
import type { ResolvedAppScope } from 'v5/core/app-scope/appScope.types';
import { appRouting } from 'v5/core/routing/appRouting';
import { EnterpriseMenuCollapsibleSkeleton } from 'v5/features/core/enterprise/EnterpriseMenuCollapsibleSkeleton';
import { EnterpriseNewTeamMenuItem } from 'v5/features/core/enterprise/EnterpriseNewTeamMenuItem';
import { EnterpriseSidebarEmptyState } from 'v5/features/core/enterprise/EnterpriseSidebarEmptyState';
import { EnterpriseTeamIcon } from 'v5/features/core/enterprise/EnterpriseTeamIcon';
import { myServices } from 'v5/services/services.connected.app';
import { useTranslation } from 'react-i18next';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from 'v5/design-system/Tooltip';
import { MenuCollapsible, MenuItem } from 'v5/patterns/sidebar/Menu';
import { SidebarGroup } from 'v5/patterns/sidebar/Sidebar';
import { cn } from 'v5/platform/dom/cn';
import { NavLink } from 'v5/platform/react-router';

export type EnterpriseTeamsNavSectionProps = {
  teamsRd: RemoteData<EnterpriseTeam[]>;
  organizationId: string;
  workspaceSlug: string;
  scope: ResolvedAppScope | undefined;
  productionTypes: Array<{ id: number; name: string }> | undefined;
  onCreateProduction: (
    productionTypeId?: number,
    scopeOverride?: {
      enterpriseWorkspaceId?: number;
      enterpriseTeamId?: number;
    }
  ) => void;
  canManage?: boolean;
  onCreateTeam?: () => void;
};

export function EnterpriseTeamsNavSection({
  teamsRd,
  organizationId,
  workspaceSlug,
  scope,
  productionTypes,
  onCreateProduction,
  canManage = false,
  onCreateTeam
}: EnterpriseTeamsNavSectionProps) {
  const { t } = useTranslation('core', { keyPrefix: 'enterprise' });

  return (
    <SidebarGroup label={t('teamsSectionLabel')}>
      {rd
        .journey(teamsRd)
        .wait(<EnterpriseMenuCollapsibleSkeleton count={3} />)
        .catch(() => (
          <div className="px-3 py-2 text-[13px] text-fg-muted">
            {t('teamsUnavailable')}
          </div>
        ))
        .map(teams =>
          teams.length === 0 ? (
            <EnterpriseSidebarEmptyState messageKey="emptyTeams" />
          ) : (
            teams.map(team => (
              <TeamNavCollapsible
                key={team.id}
                team={team}
                organizationId={organizationId}
                workspaceSlug={workspaceSlug}
                defaultOpen={
                  scope?.level === 'team' && scope.teamSlug === team.slug
                }
                productionTypes={productionTypes}
                onCreateProduction={onCreateProduction}
              />
            ))
          )
        )}
      {canManage && onCreateTeam ? (
        <EnterpriseNewTeamMenuItem onClick={onCreateTeam} />
      ) : null}
    </SidebarGroup>
  );
}

interface TeamMenuItemWithCreateButtonProps {
  to: string;
  icon?: React.ReactNode;
  level?: number;
  onCreateClick?: (e: React.MouseEvent) => void;
  createTooltip: string;
  children: React.ReactNode;
  isActiveOverride?: boolean;
  autoSelect?: boolean;
}

function TeamMenuItemWithCreateButton({
  to,
  icon,
  level = 0,
  onCreateClick,
  createTooltip,
  children,
  isActiveOverride,
  autoSelect
}: TeamMenuItemWithCreateButtonProps) {
  return (
    <NavLink to={to} isActiveOverride={isActiveOverride}>
      <MenuItem
        className="group/menu-item relative"
        level={level}
        icon={icon}
        autoSelect={autoSelect}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="">
              <div
                role="button"
                className={cn(
                  'absolute right-1.5 top-1.5 size-7 flex items-center justify-center group-hover/menu-item:opacity-100 opacity-0 transition-all',
                  'rounded bg-transparent! backdrop-blur-3xl hover:bg-bg-emphasis/5!'
                )}
                onClick={onCreateClick}
              >
                <PlusIcon className="size-4 shrink-0" />
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="right">{createTooltip}</TooltipContent>
        </Tooltip>
        {children}
      </MenuItem>
    </NavLink>
  );
}

interface TeamProductionTypeMenuItemProps {
  productionType: { id: number; name: string };
  href: string;
  typeMatchPath: string;
  detailMatchPath: string;
  organizationId: string;
  onCreateClick: (productionTypeId: number) => void;
}

function TeamProductionTypeMenuItem({
  productionType,
  href,
  typeMatchPath,
  detailMatchPath,
  organizationId,
  onCreateClick
}: TeamProductionTypeMenuItemProps) {
  const productionTypeName = productionType.name || 'Production';
  const createTooltip = `Create new ${productionTypeName.toLowerCase()}`;

  const isOnProductionTypePage = useRouteMatch({
    path: typeMatchPath,
    exact: true
  });

  const currentDetailRoute = useRouteMatch<{ id: string }>({
    path: detailMatchPath
  });

  const currentProduction = myServices.productionService.useProduction(
    maybe.map(currentDetailRoute?.params?.id, Number),
    organizationId
  );
  const isActiveFromProduction = rd.mapOrElse(
    currentProduction,
    production => production.production_type_id === productionType.id,
    false
  );

  const handleCreateClick = React.useCallback(() => {
    onCreateClick(productionType.id);
  }, [onCreateClick, productionType.id]);

  return (
    <TeamMenuItemWithCreateButton
      level={2}
      to={href}
      icon={<Camera className="size-4" />}
      onCreateClick={handleCreateClick}
      createTooltip={createTooltip}
      isActiveOverride={!!isOnProductionTypePage || isActiveFromProduction}
    >
      {productionTypeName}
    </TeamMenuItemWithCreateButton>
  );
}

function TeamNavCollapsible({
  team,
  organizationId,
  workspaceSlug,
  defaultOpen,
  productionTypes,
  onCreateProduction
}: {
  team: EnterpriseTeam;
  organizationId: string;
  workspaceSlug: string;
  defaultOpen: boolean;
  productionTypes: Array<{ id: number; name: string }> | undefined;
  onCreateProduction: (
    productionTypeId?: number,
    scopeOverride?: {
      enterpriseWorkspaceId?: number;
      enterpriseTeamId?: number;
    }
  ) => void;
}) {
  const teamRoutes = appRouting
    .org(organizationId)
    .workspace(workspaceSlug)
    .team(team.slug);

  const isOnTeamRoute = useRouteMatch({
    path: teamRoutes.root(),
    exact: false
  });

  const isOnProductionsRoot = useRouteMatch({
    path: teamRoutes.productions.root(),
    exact: true
  });

  const isOnTeamProductionRoute = useRouteMatch({
    path: teamRoutes.productions.root(),
    exact: false
  });

  return (
    <MenuCollapsible
      icon={
        <EnterpriseTeamIcon
          name={team.name}
          iconUrl={team.iconUrl}
          iconColor={team.iconColor}
        />
      }
      label={team.name}
      defaultOpen={defaultOpen || !!isOnTeamRoute}
    >
      <MenuCollapsible
        icon={<Camera className="size-4" />}
        label="Productions"
        level={1}
        expandOnChildSelect
        defaultOpen={!!isOnTeamProductionRoute}
      >
        <TeamMenuItemWithCreateButton
          level={2}
          to={teamRoutes.productions.root()}
          icon={<List className="size-4" />}
          onCreateClick={() =>
            onCreateProduction(undefined, {
              enterpriseWorkspaceId: team.workspaceId,
              enterpriseTeamId: team.id
            })
          }
          createTooltip="Create new production"
          isActiveOverride={!!isOnProductionsRoot}
          autoSelect
        >
          All
        </TeamMenuItemWithCreateButton>
        {productionTypes?.map(productionType => (
          <TeamProductionTypeMenuItem
            key={productionType.id}
            productionType={productionType}
            href={teamRoutes.productions.type(productionType.id)}
            typeMatchPath={teamRoutes.productions.type(productionType.id)}
            detailMatchPath={teamRoutes.productions.detail()}
            organizationId={organizationId}
            onCreateClick={typeId =>
              onCreateProduction(typeId, {
                enterpriseWorkspaceId: team.workspaceId,
                enterpriseTeamId: team.id
              })
            }
          />
        ))}
      </MenuCollapsible>
      <NavLink to={teamRoutes.shortlists.root()}>
        <MenuItem level={1} icon={<GalleryHorizontal className="size-4" />}>
          Shortlists
        </MenuItem>
      </NavLink>
      <NavLink to={teamRoutes.callsheets.root()}>
        <MenuItem level={1} icon={<FileText className="size-4" />}>
          Callsheets
        </MenuItem>
      </NavLink>
      <NavLink to={teamRoutes.frontdesk.root()}>
        <MenuItem level={1} icon={<Clapperboard className="size-4" />}>
          Front desk
        </MenuItem>
      </NavLink>
    </MenuCollapsible>
  );
}
